import { Download, FileText, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@stockline/ui/components/button";
import { getFriendlyMimeLabel } from "@/lib/mimeLabels";
import { downloadMedia } from "./useChatMediaUrl";

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Generic file attachment (anything that isn't viewable inline — not an
 * image/pdf/audio/video). This is the only media card that keeps the
 * name/type/size details, since downloading is the whole point here.
 */
export function ChatMediaDocument({
  url,
  mimeType,
  name,
  size,
}: {
  readonly url: string | null;
  readonly mimeType: string;
  readonly name: string;
  readonly size: number;
}) {
  const { t } = useTranslation();
  const label = getFriendlyMimeLabel(mimeType, name);
  const details = [label, size > 0 ? formatFileSize(size) : null]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      className="flex w-full items-center gap-3 rounded-2xl border bg-card p-3"
      data-testid="chat-media-document"
    >
      <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-muted text-muted-foreground">
        {url ? (
          <FileText className="size-5" />
        ) : (
          <Loader2 className="size-5 animate-spin" />
        )}
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-medium">{name}</span>
        {details && (
          <span className="text-xs text-muted-foreground">{details}</span>
        )}
      </span>
      <Button
        variant="ghost"
        size="icon"
        disabled={!url}
        onClick={() => url && void downloadMedia(url, name)}
        aria-label={t("chat.download", "Télécharger")}
        className="size-9 shrink-0 rounded-full"
      >
        <Download className="size-4" />
      </Button>
    </div>
  );
}
